import {
  FileInfo,
  GalaxyBaseUploaderServiceInterface,
  GalaxyImageUploaderService,
  UploadResponse,
} from '@vendasta/galaxy/uploader';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Environment, EnvironmentService } from '@galaxy/core';
import { map, switchMap, take } from 'rxjs/operators';
import { ImageGenerationDialogComponent } from '@galaxy/content-generation';
import { v4 as uuidv4 } from 'uuid';
import { MatDialog } from '@angular/material/dialog';
import { SENDER_INJECTION_TOKEN } from '../shared';
import { Sender } from '@vendasta/email';

export const EMAILBUILDER_UPLOADER_SERVICE_TOKEN = new InjectionToken<GalaxyBaseUploaderServiceInterface>(
  '[EmailBuilderUploaderService]: token for emailbuilder uploader',
);

@Injectable()
export class ImageUploadService extends GalaxyImageUploaderService {
  constructor(
    http: HttpClient,
    private readonly env: EnvironmentService,
    @Optional() @Inject(SENDER_INJECTION_TOKEN) private readonly sender$: Observable<Sender>,
    @Optional() @Inject(EMAILBUILDER_UPLOADER_SERVICE_TOKEN) private readonly uploaderService: ImageUploadService,
    private dialog: MatDialog,
  ) {
    super(http);
    if (!uploaderService && !sender$) {
      throw new Error(
        'Invalid dependencies. Either SENDER_INJECTION_TOKEN or EMAILBUILDER_UPLOADER_SERVICE_TOKEN must be provided',
      );
    }
  }

  buildRequest(fileInfo: FileInfo): Observable<UploadResponse> {
    if (this.uploaderService) {
      return this.uploaderService.buildRequest(fileInfo);
    }
    this.uploadUrl = 'https://email-demo.apigateway.co/content/image/upload';
    if (this.env.getEnvironment() === Environment.PROD) {
      this.uploadUrl = 'https://email-prod.apigateway.co/content/image/upload';
    }
    return this.sender$.pipe(
      map((sender: Sender) => new HttpParams().set('senderType', sender.type).set('senderId', sender.id)),
      switchMap((params) => super.buildRequest(fileInfo, params)),
      take(1),
    );
  }

  async generateAndUploadImages(): Promise<Observable<string>> {
    const dialogRef = this.dialog.open(ImageGenerationDialogComponent, {
      width: '830px',
      height: 'auto',
    });

    return await dialogRef
      .afterClosed()
      .toPromise()
      .then((file) => {
        if (file) {
          const imageToUpload = {
            context: { uuid: uuidv4() },
            name: file.name,
            file: file,
          } as FileInfo;

          return this.buildRequest(imageToUpload).pipe(map((resp) => resp.data.url));
        }
      });
  }
}
